import React, { useCallback, useEffect, useState, useRef } from "react";
import PropTypes from 'prop-types';
import '../../styles/MultiRangeSlider.css';

/**
 * Multi range slider component
 * 
 * @param {object} props
 * @param {number} props.min - minimum number in range
 * @param {number} props.max - maximum number in range
 * @param {Function} props.onChange - function that runs when a change is made
 * @param {string} props.label - label above slider
 * @param {number} [props.initialMin] - optional initial minimum value
 * @param {number} [props.initialMax] - optional initial maximum value
 */
const MultiRangeSlider = ({ min, max, onChange, label, initialMin, initialMax }) => {
    const [minVal, setMinVal] = useState(initialMin !== undefined ? initialMin : min);
    const [maxVal, setMaxVal] = useState(initialMax !== undefined ? initialMax : max);

    const [minInput, setMinInput] = useState(String(initialMin !== undefined ? initialMin : min));
    const [maxInput, setMaxInput] = useState(String(initialMax !== undefined ? initialMax : max));

    const range = useRef(null);

    const getPercent = useCallback(
        (value) => ((value - min) / (max - min)) * 100,
        [min, max]
    );

    useEffect(() => {
        if (range.current) {
            const minPercent = getPercent(minVal);
            const maxPercent = getPercent(maxVal);
            range.current.style.left = `${minPercent}%`;
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [minVal, maxVal, getPercent]);

    useEffect(() => {
        onChange({ min: minVal, max: maxVal });
    }, [minVal, maxVal, onChange]);

    useEffect(() => {
        if (initialMin !== undefined || initialMax !== undefined) {
            const newMin = initialMin !== undefined ? initialMin : min;
            const newMax = initialMax !== undefined ? initialMax : max;
            setMinVal(newMin);
            setMaxVal(newMax);
            setMinInput(String(newMin));
            setMaxInput(String(newMax));
        }
    }, [initialMin, initialMax, min, max]);

    const handleMinSliderChange = (event) => {
        const value = Math.min(Number(event.target.value), maxVal);
        setMinVal(value);
        setMinInput(String(value));
    };

    const handleMaxSliderChange = (event) => {
        const value = Math.max(Number(event.target.value), minVal);
        setMaxVal(value);
        setMaxInput(String(value));
    };

    const handleMinBlur = () => {
        let value = parseInt(minInput, 10);
        if (isNaN(value) || value < min || value > maxVal) {
            value = min;
        }
        setMinVal(value);
        setMinInput(String(value));
    };

    const handleMaxBlur = () => {
        let value = parseInt(maxInput, 10);
        if (isNaN(value) || value > max || value < minVal) {
            value = max;
        }
        setMaxVal(value);
        setMaxInput(String(value));
    };

    return (
        <div style={{ marginBottom: '8%' }}>
            {label && <label className="slider-label mb-3">{label}</label>}

            <div className="multi-range-slider-container">
                <input
                    type="range"
                    min={min}
                    max={max}
                    value={minVal}
                    onChange={handleMinSliderChange}
                    className="thumb thumb--left"
                    style={{ zIndex: minVal > max - 100 ? "5" : undefined }}
                />

                <input
                    type="range"
                    min={min}
                    max={max}
                    value={maxVal}
                    onChange={handleMaxSliderChange}
                    className="thumb thumb--right"
                />

                <div className="slider">
                    <div className="slider__track" />
                    <div ref={range} className="slider__range" />

                    <div className="slider__left-value">
                        <input
                            type="text"
                            value={minInput}
                            placeholder={String(min)}
                            style={{ width: "40px", height: "25px", marginLeft: "-10px", fontSize: "12px" }}
                            onChange={(e) => setMinInput(e.target.value.replace(/\D/g, ""))}
                            onBlur={handleMinBlur}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleMinBlur(); }}
                        />
                    </div>

                    <div className="slider__right-value">
                        <input
                            type="text"
                            value={maxInput}
                            placeholder={String(max)}
                            style={{ width: "40px", height: "25px", marginLeft: "-10px", fontSize: "12px" }}
                            onChange={(e) => setMaxInput(e.target.value.replace(/\D/g, ""))}
                            onBlur={handleMaxBlur}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleMaxBlur(); }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

MultiRangeSlider.propTypes = {
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    initialMin: PropTypes.number,
    initialMax: PropTypes.number,
};

export default MultiRangeSlider;
